import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { fmtCurrency } from "@/lib/format";
import { Bell, X } from "lucide-react";

export function AlertsBell() {
    const qc = useQueryClient();
    const navigate = useNavigate();
    const { data } = useQuery({
        queryKey: ["alerts"],
        queryFn: async () => (await api.get("/alerts")).data,
        refetchInterval: 60000,
        refetchOnWindowFocus: true,
    });
    const triggered = (data?.alerts || []).filter((a) => a.status === "triggered");
    const count = triggered.length;

    const dismiss = async (id) => {
        await api.patch(`/alerts/${id}`, { action: "dismiss" });
        qc.invalidateQueries({ queryKey: ["alerts"] });
    };

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="alerts-bell-btn" title="Price alerts">
                    <Bell className={`w-[18px] h-[18px] ${count ? "text-primary" : ""}`} />
                    {count > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-loss text-white text-[10px] font-bold flex items-center justify-center num" data-testid="alerts-bell-count">{count}</span>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-0" data-testid="alerts-bell-popover">
                <div className="px-4 py-3 border-b border-border font-heading font-semibold text-sm">Price Alerts</div>
                <div className="max-h-80 overflow-y-auto">
                    {count === 0 ? (
                        <p className="px-4 py-6 text-sm text-muted-foreground text-center" data-testid="alerts-none">No triggered alerts. You'll see a flag here when a target is crossed.</p>
                    ) : triggered.map((a) => (
                        <div key={a.id} className="flex items-center justify-between gap-2 px-4 py-2.5 hover:bg-muted/40 border-b border-border/50" data-testid={`alert-triggered-${a.ticker}`}>
                            <button className="text-left min-w-0 flex-1" onClick={() => navigate(`/security/${a.ticker}`)}>
                                <div className="font-semibold num text-sm">{a.ticker}</div>
                                <div className="text-xs text-muted-foreground">
                                    crossed {a.direction} {fmtCurrency(a.target_price, { decimals: 2 })}{a.current_price != null ? ` · now ${fmtCurrency(a.current_price, { decimals: 2 })}` : ""}
                                </div>
                            </button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => dismiss(a.id)} title="Dismiss" data-testid={`alert-dismiss-${a.ticker}`}><X className="w-3.5 h-3.5" /></Button>
                        </div>
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    );
}
